import { normalizeAuthPayload } from 'actions/authentication/authentication';
import type { RootState } from 'reducers/types';
import type { Dispatch } from 'redux';
import { users } from 'repositories';
import {
  autoLogoutEnabled,
  getCurrentLocale,
  getEmail,
  getFieldValue,
  getRefreshToken,
  isFetching,
  isImpersonatingUser,
  isLoggedIn,
} from 'selectors';
import { auth } from 'services/payex/auth/auth';
import { removeLocalStorageUserKey } from 'store/storage/localStorage';
import { cookies } from 'utils/cookies/cookies';
import { errorNotifier } from 'utils/errorNotifier/errorNotifier';
import { localStorage, sessionStorage } from 'utils/storage';
import {
  AUTH_LOCALSTORAGE_KEY,
  AUTHENTICATION_COOKIE_NAME,
  CHANGE_USER_PASSWORD_SUCCESS,
  CREATE_USER_FAILURE,
  CREATE_USER_REQUEST,
  CREATE_USER_SUCCESS,
  FETCH_CREDIT_CARDS_FAILURE,
  FETCH_CREDIT_CARDS_REQUEST,
  FETCH_CREDIT_CARDS_SUCCESS,
  FETCH_USER_FAILURE,
  FETCH_USER_REQUEST,
  FETCH_USER_SUCCESS,
  HTTPStatuses,
  INTERNAL_SERVER_FAILURE,
  LOGIN_USER_SUCCESS,
  LOGOUT_USER_REQUEST,
  LOGOUT_USER_SUCCESS,
  REMOVE_CREDIT_CARD_FAILURE,
  REMOVE_CREDIT_CARD_REQUEST,
  REMOVE_CREDIT_CARD_SUCCESS,
  SAVE_CARD_FAILURE,
  SAVE_CARD_REQUEST,
  SAVE_CARD_SUCCESS,
  SENDER_EMAIL_FIELD,
  UPDATE_USER_FAILURE,
  UPDATE_USER_REQUEST,
  UPDATE_USER_SUCCESS,
  USER_EXISTS_FAILURE,
  USER_EXISTS_REQUEST,
  USER_EXISTS_SUCCESS,
} from '../../../constants';

type createUserProps = {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  captchaResponse?: string;
};

export const signupUser =
  (attributes: createUserProps) =>
  async (dispatch: Dispatch, getState: () => RootState) => {
    const state = getState();
    const isFetchingUser = isFetching(state, 'user');
    const locale = getCurrentLocale(state);

    if (isFetchingUser) {
      return Promise.resolve();
    }

    dispatch({ type: CREATE_USER_REQUEST });

    const { email, password, firstName, lastName, captchaResponse } =
      attributes;
    const payload = {
      captchaResponse,
      email,
      firstName,
      lastName,
      locale,
      password,
      passwordConfirmation: password,
    };

    try {
      const { user, token } = await auth.signup(payload, locale);

      const authentication = normalizeAuthPayload(token);

      dispatch({
        type: CREATE_USER_SUCCESS,
        payload: user,
      });

      return dispatch({
        type: LOGIN_USER_SUCCESS,
        payload: { authentication, user },
      });
    } catch (error) {
      if (error.status === HTTPStatuses.INTERNAL_SERVER_ERROR) {
        dispatch({ type: INTERNAL_SERVER_FAILURE });
      }

      dispatch({
        type: CREATE_USER_FAILURE,
      });
      throw error;
    }
  };

export const updateUser =
  attributes =>
  async (dispatch: Dispatch, getState: () => RootState, { apiV3 }) => {
    const state = getState();
    const isFetchingUser = isFetching(state, 'user');
    const locale = getCurrentLocale(state);

    if (isFetchingUser) {
      return Promise.resolve();
    }

    dispatch({ type: UPDATE_USER_REQUEST });

    const {
      firstName,
      lastName,
      email,
      password,
      passwordConfirmation,
      currentPassword,
    } = attributes;

    const payload = {
      first_name: firstName,
      last_name: lastName,
      email,
      current_password: currentPassword,
      password,
      password_confirmation: passwordConfirmation,
      locale,
    };

    try {
      const response = await apiV3.users.update(payload);

      return dispatch({
        type: UPDATE_USER_SUCCESS,
        payload: response,
      });
    } catch (error) {
      dispatch({
        type: UPDATE_USER_FAILURE,
      });
      throw error;
    }
  };

export const fetchUser =
  () =>
  async (dispatch: Dispatch, getState: () => RootState, { apiV3 }) => {
    const state = getState();
    const isFetchingUser = isFetching(state, 'user');

    if (isFetchingUser) {
      return;
    }

    dispatch({ type: FETCH_USER_REQUEST });

    const locale = getCurrentLocale(state);

    try {
      const response = await apiV3.users.fetchUser({
        locale,
      });
      if (!response) {
        return dispatch({ type: FETCH_USER_FAILURE });
      }

      dispatch({
        type: FETCH_USER_SUCCESS,
        payload: response,
      });
    } catch (error) {
      dispatch({
        type: FETCH_USER_FAILURE,
        payload: error,
      });
    }
  };

export const cleanUserData =
  () => (dispatch: Dispatch, getState: () => RootState) => {
    const state = getState();
    const isLoggingOut = isFetching(state, 'logout');
    if (isLoggingOut) {
      return Promise.resolve();
    }

    dispatch({ type: LOGOUT_USER_REQUEST });

    localStorage.removeItem(AUTH_LOCALSTORAGE_KEY);
    sessionStorage.removeItem(AUTH_LOCALSTORAGE_KEY);
    cookies.delete(AUTHENTICATION_COOKIE_NAME);
    removeLocalStorageUserKey();

    return dispatch({ type: LOGOUT_USER_SUCCESS });
  };

export const cleanUnauthorizedUserData =
  () => (dispatch: Dispatch, getState: () => RootState) => {
    const state = getState();
    const isImpersonating = isImpersonatingUser(state);

    if (!isImpersonating) {
      dispatch(cleanUserData());
    }
  };

export const logout =
  () =>
  async (dispatch: Dispatch, getState: () => RootState, { apiV3 }) => {
    const state = getState();
    const payload = { refresh_token: getRefreshToken(state) };

    dispatch(cleanUserData());

    try {
      return await apiV3.authentication.revoke(payload);
    } catch (error) {
      const { status = '' } = error;

      if (status !== HTTPStatuses.UNAUTHORIZED) {
        errorNotifier.notify(error);
      }
    }
  };

export const autoLogout =
  () => (dispatch: Dispatch, getState: () => RootState) => {
    const state = getState();
    if (!isLoggedIn(state)) {
      return;
    }
    if (autoLogoutEnabled(state)) {
      return dispatch(logout());
    }
  };

export const resetUserPassword = (userEmail: string) => () => {
  return auth.resetPassword({ userEmail });
};

export const changeUserPassword =
  ({ password, resetPasswordToken }) =>
  async (dispatch: Dispatch, _getState: () => RootState, { apiV3 }) => {
    await apiV3.users.changePassword({
      password,
      resetPasswordToken,
    });
    return dispatch({
      type: CHANGE_USER_PASSWORD_SUCCESS,
    });
  };

export const userExists =
  () => async (dispatch: Dispatch, getState: () => RootState) => {
    dispatch({
      type: USER_EXISTS_REQUEST,
    });

    const state = getState();
    const email = getFieldValue(state, SENDER_EMAIL_FIELD);
    const token = cookies.read('csrf');
    const response = await users.userExists({ email, token });

    dispatch({
      type: response ? USER_EXISTS_SUCCESS : USER_EXISTS_FAILURE,
    });
  };

export const fetchCreditCards =
  () =>
  async (dispatch: Dispatch, _getState: () => RootState, { wallet }) => {
    dispatch({
      type: FETCH_CREDIT_CARDS_REQUEST,
    });

    try {
      const response = await wallet.fetchCreditCards();

      dispatch({
        type: FETCH_CREDIT_CARDS_SUCCESS,
        payload: response,
      });
    } catch (_error) {
      dispatch({ type: FETCH_CREDIT_CARDS_FAILURE });
    }
  };

export const removeCreditCard =
  token =>
  async (dispatch: Dispatch, _getState: () => RootState, { wallet }) => {
    dispatch({
      type: REMOVE_CREDIT_CARD_REQUEST,
    });

    try {
      await wallet.removeCreditCard(token);

      dispatch({
        type: REMOVE_CREDIT_CARD_SUCCESS,
        payload: token,
      });
    } catch (_error) {
      dispatch({ type: REMOVE_CREDIT_CARD_FAILURE });
    }
  };

export const saveCard =
  cardDetails =>
  async (dispatch: Dispatch, getState: () => RootState, { wallet }) => {
    dispatch({ type: SAVE_CARD_REQUEST });

    try {
      const state = getState();
      const email = getEmail(state);
      await wallet.saveCard({ ...cardDetails, email });

      dispatch({
        type: SAVE_CARD_SUCCESS,
      });

      return dispatch(fetchCreditCards());
    } catch (error) {
      dispatch({ type: SAVE_CARD_FAILURE });
      throw error;
    }
  };
